import type { OrderEventDocumentRef } from '@klear/upload/order-event-documents'
import { assignTrimmedField } from '@klear/schema/patch'
import { PERMISSION_ENTITY_OPERATIONAL } from '@klear/constants'
import type { Incoterm } from '@klear/constants/options'
import { KlearDate } from '@klear/date'
import { Money } from '@klear/money'
import isString from 'lodash/isString'
import toString from 'lodash/toString'
import values from 'lodash/values'
import type { OperationalCreateWire, OperationalUpdateWire } from '@klear/operational/operational.wire'
import type { Operational } from '@klear/operational/operational'

export type OperationalForm = {
  type: typeof PERMISSION_ENTITY_OPERATIONAL
  title: string
  supplierId: string
  number: string
  incoterm: string
  amount: Money
  effectiveDate: KlearDate
  endDate?: KlearDate
  notes: string
}

export type OperationalFormErrorMap = Record<string, string | undefined>

export type OperationalEventData = Partial<Omit<OperationalCreateWire, 'orderId'>> & { type?: string }

export const EMPTY_OPERATIONAL_FORM: OperationalForm = {
  type: PERMISSION_ENTITY_OPERATIONAL,
  title: '',
  supplierId: '',
  number: '',
  incoterm: '',
  amount: new Money(),
  effectiveDate: KlearDate.today(),
  notes: ''
}

function parseKlearDate(value: unknown): KlearDate | undefined {
  if (KlearDate.is(value)) return value
  if (isString(value) && value.length > 0) return KlearDate.parse(value)
  return undefined
}

export function operationalFormFromStored(data: OperationalEventData | null): OperationalForm {
  if (data == null) return { ...EMPTY_OPERATIONAL_FORM, effectiveDate: KlearDate.today() }

  const endDate = parseKlearDate(data.endDate)
  return {
    type: PERMISSION_ENTITY_OPERATIONAL,
    title: data.title ?? '',
    supplierId: data.supplierId != null ? toString(data.supplierId) : '',
    number: data.number ?? '',
    incoterm: data.incoterm ?? '',
    amount: data.amount != null ? new Money(data.amount) : new Money(),
    effectiveDate: parseKlearDate(data.effectiveDate) ?? KlearDate.today(),
    endDate,
    notes: data.notes ?? ''
  }
}

export function validateOperationalDetails(form: OperationalForm): OperationalFormErrorMap {
  const errors: OperationalFormErrorMap = {}
  if (!form.title.trim()) {
    errors.title = 'Title is required'
  }
  if (!form.supplierId.trim()) {
    errors.supplierId = 'Supplier is required'
  }
  return errors
}

export function operationalFormToEventData(form: OperationalForm): OperationalEventData {
  const data: OperationalEventData = {
    type: form.type,
    title: form.title.trim(),
    supplierId: form.supplierId.trim(),
    effectiveDate: form.effectiveDate.toJSON(),
    currency: form.amount.currency,
    amount: form.amount.toJSON()
  }

  assignTrimmedField(data, 'notes', form.notes)

  assignTrimmedField(data, 'number', form.number)
  assignTrimmedField(data, 'incoterm', form.incoterm)
  if (form.endDate != null) {
    data.endDate = form.endDate.toJSON()
  }

  return data
}

export function hasOperationalFormErrors(errors: OperationalFormErrorMap): boolean {
  return values(errors).some(Boolean)
}

export function clearOperationalFormErrors(
  errors: OperationalFormErrorMap,
  paths: readonly (keyof OperationalForm | string)[]
): OperationalFormErrorMap {
  const next = { ...errors }
  for (const path of paths) {
    delete next[path]
  }
  return next
}

export type OperationalFieldChange = <K extends keyof OperationalForm>(field: K, value: OperationalForm[K]) => void

export function operationalFormToCreateWire(form: OperationalForm, orderId: number): OperationalCreateWire {
  const wire: OperationalCreateWire = {
    orderId,
    title: form.title.trim(),
    currency: form.amount.currency,
    effectiveDate: form.effectiveDate.toJSON()
  }

  assignTrimmedField(wire, 'supplierId', form.supplierId)
  assignTrimmedField(wire, 'number', form.number)
  assignTrimmedField(wire, 'incoterm', form.incoterm)
  if (form.amount.greaterThan(form.amount.zero)) wire.amount = form.amount.toJSON()
  if (form.endDate != null) wire.endDate = form.endDate.toJSON()
  assignTrimmedField(wire, 'notes', form.notes)

  return wire
}

export function operationalFormToUpdateWire(form: OperationalForm): OperationalUpdateWire {
  const wire: OperationalUpdateWire = {
    title: form.title.trim(),
    currency: form.amount.currency,
    effectiveDate: form.effectiveDate.toJSON(),
    supplierId: form.supplierId.trim(),
    number: form.number.trim() || null,
    incoterm: form.incoterm.trim() || null,
    endDate: form.endDate != null ? form.endDate.toJSON() : null,
    notes: form.notes.trim() || null
  }
  wire.amount = form.amount.greaterThan(form.amount.zero) ? form.amount.toJSON() : null

  return wire
}

export type OperationalTimelineForm = {
  effectiveDate: KlearDate
  endDate?: KlearDate
  number?: string
  notes?: string
  title: string
  supplierId?: string | number
  incoterm?: Incoterm
  amount: Money
}

export type OperationalEventFormValues = OperationalTimelineForm & {
  type: typeof PERMISSION_ENTITY_OPERATIONAL
  documents?: OrderEventDocumentRef[]
  files?: File[]
}

export type OperationalTimelineFormErrorMap = Record<string, string | undefined>

export type OperationalParentDefaults = {
  amount?: Money
  endDate?: KlearDate
  supplierId?: string | number
}

export function parentDefaultsFromOperational(read: Operational): OperationalParentDefaults {
  return {
    amount: read.amount.greaterThan(read.amount.zero) ? read.amount : undefined,
    endDate: read.endDate,
    supplierId: read.supplierId
  }
}

export function operationalToStoredEventData(read: Operational): OperationalEventData {
  const data: OperationalEventData = {
    type: PERMISSION_ENTITY_OPERATIONAL,
    title: read.title,
    amount: read.amount.toJSON(),
    effectiveDate: read.effectiveDate.toJSON(),
    currency: read.amount.currency
  }

  if (read.supplierId != null) data.supplierId = toString(read.supplierId)
  if (read.number) data.number = read.number
  if (read.incoterm) data.incoterm = read.incoterm
  if (read.endDate != null) data.endDate = read.endDate.toJSON()
  if (read.notes) data.notes = read.notes

  return data
}

export function operationalToEventForm(read: Operational): OperationalEventFormValues {
  return {
    type: PERMISSION_ENTITY_OPERATIONAL,
    effectiveDate: read.effectiveDate,
    endDate: read.endDate,
    number: read.number,
    notes: read.notes,
    documents: [],
    title: read.title,
    supplierId: read.supplierId != null ? `${read.supplierId}` : '',
    incoterm: (read.incoterm as Incoterm | undefined) ?? undefined,
    amount: read.amount
  }
}

export function createOperationalEventForm(parent?: OperationalParentDefaults): OperationalEventFormValues {
  return {
    type: PERMISSION_ENTITY_OPERATIONAL,
    effectiveDate: KlearDate.today(),
    notes: '',
    amount: parent?.amount ?? new Money(),
    title: '',
    supplierId: '',
    documents: []
  }
}

export function validateOperationalEventForm(form: OperationalTimelineForm): OperationalTimelineFormErrorMap {
  const errors: OperationalTimelineFormErrorMap = {}
  if (!form.title || form.title.trim().length === 0) {
    errors.title = 'Title is required'
  }
  return errors
}

export function operationalEventFormToCreateWire(
  form: OperationalTimelineForm,
  orderId: number,
  parentEventId: number | undefined
): OperationalCreateWire {
  const wire: OperationalCreateWire = {
    orderId,
    title: form.title,
    currency: form.amount.currency,
    effectiveDate: form.effectiveDate.toJSON()
  }
  if (form.supplierId != null && form.supplierId !== '') wire.supplierId = form.supplierId
  if (form.number) wire.number = form.number
  if (form.incoterm) wire.incoterm = form.incoterm
  if (form.amount.greaterThan(form.amount.zero)) wire.amount = form.amount.toJSON()
  if (form.endDate != null) wire.endDate = form.endDate.toJSON()
  if (form.notes) wire.notes = form.notes
  if (parentEventId != null && parentEventId > 0) wire.parentEventId = parentEventId
  return wire
}

export function operationalEventFormToUpdateWire(
  form: OperationalTimelineForm,
  parentEventId: number | undefined
): OperationalUpdateWire {
  const wire: OperationalUpdateWire = {
    title: form.title,
    currency: form.amount.currency,
    effectiveDate: form.effectiveDate.toJSON(),
    supplierId: form.supplierId != null && form.supplierId !== '' ? form.supplierId : undefined,
    number: form.number?.trim() || null,
    incoterm: form.incoterm?.trim() || null,
    endDate: form.endDate != null ? form.endDate.toJSON() : null,
    notes: form.notes?.trim() || null
  }
  wire.amount = form.amount.greaterThan(form.amount.zero) ? form.amount.toJSON() : null
  if (parentEventId != null && parentEventId > 0) wire.parentEventId = parentEventId
  return wire
}
